import { Paid } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import React, { useState } from 'react'
import useCustomerOpportunities from '../../hooks/api/useCustomerOpportunities'
import CustomerOpportunity from '../components/CustomerOpportunity/CustomerOpportunity'
import GenericLoadError from '../../common/GenericLoadError'
import CustomerHeader from '../components/CustomerHeader'
import CustomerRevenueSummary from '../components/CustomerRevenueSummary'
import Tooltip from '../../common/Tooltip'

// NICE TO HAVE: filters/pagination/sorting

const Deals = ({
  id,
  pipelines,
  products,
  billingCycleTypes,
  customerQuery,
  customerStatusTypesData,
  customerTicketsQuery,
  types,
  dropdowns,
}) => {
  const {
    customerOpportunities,
    saveCurrentOpportunity,
    setCurrentOpportunity,
    isLoading,
    closeOpportunity,
    hasLoadError,
    deleteOpportunity,
    updateOpportunityWithId,
    saveOpportunity,
    ..._customerOpportunities
  } = useCustomerOpportunities(id, {
    pipelines,
  })

  const [expandedAccordions, setExpandedAccordions] = useState({
    opportunity: true,
    historic: false,
    current: false,
    potential: false,
  })

  if (isLoading) return <CircularProgress />
  if (hasLoadError) return <GenericLoadError />

  const closeOpportunityWrapper = (args) => {
    return closeOpportunity(args)
  }

  const activeOpportunity = customerOpportunities.find((o) => o.isOpen)
  const historicalOpportunities = customerOpportunities.filter((o) => !o.isOpen)

  const isOpen = activeOpportunity?.isOpen

  return (
    <>
      <CustomerHeader
        customer={customerQuery.customer}
        customerStatusTypesData={customerStatusTypesData}
        customerTicketsQuery={customerTicketsQuery}
        types={types}
        Icon={Paid}
        tooltip={
          <Tooltip
            textProps={{ sx: { whiteSpace: 'pre-line' } }}
            infoText={`Within this section, you can view any new opportunities associated with this account, examine the outcomes of all completed deals—whether Won or Lost. You also have the ability to modify your closed deals and “Cancel” any pending ones.

            Note: A canceled deal is if the deal stops paying or is no longer needing your services.`}
          />
        }
      />

      <Box sx={{ mt: 2 }}>
        {customerOpportunities.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <CustomerRevenueSummary
              customer={customerQuery.customer}
              expandedAccordions={expandedAccordions}
              setExpandedAccordions={setExpandedAccordions}
            />
          </Box>
        )}

        {customerOpportunities.length === 0 && (
          <Typography>No deals for this customer</Typography>
        )}
        {activeOpportunity && (
          <CustomerOpportunity
            customer={customerQuery.customer}
            pipelines={pipelines}
            isNewOpportunity={false}
            isNewCustomer={false}
            onSave={isOpen ? saveCurrentOpportunity : () => {}}
            currentOpportunity={activeOpportunity}
            setCurrentOpportunity={isOpen ? setCurrentOpportunity : () => {}}
            saveOpportunity={isOpen ? saveCurrentOpportunity : () => {}}
            resetOpportunity={isOpen ? _customerOpportunities.reset : () => {}}
            closeOpportunity={isOpen ? closeOpportunityWrapper : () => {}}
            products={products}
            billingCycleTypes={billingCycleTypes}
            dropdowns={dropdowns}
            onDelete={() => deleteOpportunity(activeOpportunity.id)}
            canSave
            isPrimaryInDealsPage
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
          ></CustomerOpportunity>
        )}

        {historicalOpportunities.length > 0 && (
          <>
            <Typography variant={'h5'} sx={{ mb: 1, mt: 4 }}>
              <b>Historical Deals</b>
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Closed Deals: {historicalOpportunities.length}
            </Typography>
            {historicalOpportunities.map((co) => {
              const { isOpen } = co || {}
              return (
                <Box key={co.id} sx={{ mb: 2 }}>
                  <CustomerOpportunity
                    expandedAccordions={expandedAccordions}
                    setExpandedAccordions={setExpandedAccordions}
                    customer={customerQuery.customer}
                    key={co.id}
                    pipelines={pipelines}
                    isNewOpportunity={false}
                    isNewCustomer={false}
                    currentOpportunity={co}
                    setCurrentOpportunity={(val) =>
                      updateOpportunityWithId(co.id, val)
                    }
                    onSave={() => {
                      return saveOpportunity(null, co.id, co)
                    }}
                    resetOpportunity={
                      isOpen ? _customerOpportunities.reset : () => {}
                    }
                    closeOpportunity={
                      isOpen ? closeOpportunityWrapper : () => {}
                    }
                    products={products}
                    billingCycleTypes={billingCycleTypes}
                    dropdowns={dropdowns}
                    onDelete={() => deleteOpportunity(co.id)}
                    canSave
                  ></CustomerOpportunity>
                </Box>
              )
            })}
          </>
        )}
      </Box>
    </>
  )
}

export default Deals
